import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Text from '@ui/atoms/Text';
import styled from 'styled-components';
import Button from '@ui/atoms/Button';
import Link from '@ui/atoms/Link';
import Spinner from '@ui/atoms/Spinner';
import { DropFileUploaderController } from '@app/molecules/DropFileUploader';
import useAddEpisodeMutation from '@/queries/episode/useAddEpisodeMutation.hook';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
    useEpisodeEditAudioFileMutation,
    useEpisodeEditAudioFileState,
} from '@/context/EpisodeEditContext';
import useInternalRouteMapping from '@hooks/useInternalRouteMapping.hook';
import useEpisodeAudioFileSchema from '@/utils/hooks/schemas/useEpisodeAudioFileSchema.hook';
import { debounce } from 'lodash';
import { useModalToastQueue } from '@/utils/hooks/useModalToastQueue.hook';
import { ACCEPTED_AUDIO_TYPES } from '@/utils/constants';

const Container = styled.div`
    background-color: var(--white);
    display: flex;
    flex-direction: column;
    gap: 3rem;
    min-height: 100%;
    padding: 1rem;

    ${({ theme }) => theme.mediaQueries.desktopAndUp} {
        gap: 4rem;
        padding: 2rem;
    }
`;

const Header = styled.header`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const Content = styled.div`
    align-self: center;
    max-width: 1200px;
    width: 100%;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 2rem;
`;

const SpinnerWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 25rem;
`;

const EpisodeAudioFileUploadPage = () => {
    const intl = useIntl();
    const internalRouteMapping = useInternalRouteMapping();
    const { showId } = useParams();
    const addEpisodeMutation = useAddEpisodeMutation();
    const episodeEditAudioFileMutation = useEpisodeEditAudioFileMutation();
    const [_, setEpisodeEditAudioFile] = useEpisodeEditAudioFileState();
    const toast = useModalToastQueue();
    const schema = useEpisodeAudioFileSchema();

    const { control, handleSubmit, watch, setError } = useForm({
        mode: 'onChange',
        defaultValues: {
            audioFile: null,
        },
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        const subscription = watch(() => {
            handleSubmit(onSubmit)();
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const generateEpisodeName = () => {
        return intl.formatMessage(
            { defaultMessage: 'Nouvel épisode du {date} {time}' },
            {
                date: intl.formatDate(new Date(), {
                    day: '2-digit',
                    month: '2-digit',
                }),
                time: intl.formatTime(new Date()),
            },
        );
    };

    const onSkipStep = () => {
        addEpisodeMutation.mutate(
            {
                showId,
                name: generateEpisodeName(),
            },
            {
                onSuccess: (episode) => {
                    internalRouteMapping.replace('experimental.episodes.edit', {
                        showId,
                        episodeId: episode.id,
                    });
                },
                onError: () => {
                    toast.alert();
                },
            },
        );
    };

    const onSubmit = ({ audioFile }) => {
        addEpisodeMutation.mutate(
            {
                showId,
                name: generateEpisodeName(),
            },
            {
                onSuccess: (episode) => {
                    // Fire mutation but does not wait because file upload
                    // progress is displayed in episode form.
                    episodeEditAudioFileMutation.mutate(
                        { episodeId: episode.id, audioFile },
                        {
                            onSuccess: () => debounce(() => setEpisodeEditAudioFile(null), 300)(),
                            onError: () =>
                                setError('root.serverError', {
                                    type: '400',
                                    message: intl.formatMessage({
                                        defaultMessage: 'Une erreur est survenue',
                                    }),
                                }),
                        },
                    );
                    // Keep audioFile in a state to display file data in
                    // episode form during file upload
                    setEpisodeEditAudioFile(audioFile);
                    internalRouteMapping.replace('experimental.episodes.edit', {
                        showId,
                        episodeId: episode.id,
                    });
                },
                onError: () => {
                    toast.alert();
                },
            },
        );
    };

    return (
        <Container>
            <Header>
                <Button as={Link} to={`/app/show/${showId}/episodes`} variant="secondary">
                    <FormattedMessage defaultMessage="Annuler" />
                </Button>
                <Button
                    variant="ghost"
                    color="--neutral500"
                    endIcon={
                        <FontAwesomeIcon
                            icon={icon({ name: 'chevron-right', style: 'solid' })}
                            color="inherit"
                            size="sm"
                        />
                    }
                    onPress={onSkipStep}
                    data-testid="new-episode-upload-ignore-button"
                >
                    <FormattedMessage defaultMessage="Ignorer cette étape" />
                </Button>
            </Header>
            <Content>
                {addEpisodeMutation.isLoading ? (
                    <SpinnerWrapper>
                        <Spinner size={64} />
                    </SpinnerWrapper>
                ) : (
                    <Form>
                        <Text
                            fontWeight="--fw-bold"
                            variant="headingL"
                            textAlign="center"
                            data-testid="new-episode-upload-heading"
                        >
                            <FormattedMessage defaultMessage="Ajouter un nouvel épisode" />
                        </Text>
                        <DropFileUploaderController
                            control={control}
                            name="audioFile"
                            accept={ACCEPTED_AUDIO_TYPES}
                        />
                    </Form>
                )}
            </Content>
        </Container>
    );
};

export default EpisodeAudioFileUploadPage;
